import {
  similarProductsQuery,
  productDetailQuery,
  productSkuQuery,
  productEstimateQuery,
  addToEstimateMutation,
  addonProductsQuery,
  prefecturesQuery,
  deliveryDatesQuery,
  additionalOptionsQuery,
  productRelatedCategoriesQuery,
  requestRestockSizeMutation
} from '@/product/queries/index'
import { apolloClient } from '@/common/service/apolloClient'
import {
  SimilarProductResponse,
  ProductDetailResponse,
  AddonProductResponse,
  SimilarProducts,
  AdditionalOptionsResponse
} from '@/product/types'
import {
  CreateEstimateItemAttributes,
  QueryProductByPrintableSkuArgs,
  CreateUserSizeRequestInput
} from '@/types/graphqlQuery'
import { executeQuery, executeMutation } from '@/common/service'

enum SIMILAR_PRODUCT_TYPES {
  SIMILAR = 'industry',
  SERIES = 'series'
}
let getProductSkuDataSubscription: any
let changeEstimateInputsSubscription: any

const formatProductData = (data: SimilarProducts) => {
  return {
    ...data,
    items: data.items.map(item => ({
      ...item,
      minProductPrice: item.lowestPrice,
      skuDetails: item.skus.map(sku => ({
        ...sku,
        skuImage: sku.skuImage.thumbnail
      }))
    }))
  }
}

const getSimilarProducts = (id: string, offset: number, limit: number) => {
  return apolloClient
    .query<SimilarProductResponse>({
      query: similarProductsQuery,
      variables: {
        id: id,
        offset: offset,
        limit: limit,
        type: SIMILAR_PRODUCT_TYPES.SIMILAR
      }
    }).then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      const data = formatProductData(res.data.similarProductsBy)
      return Promise.resolve(data)
    }).catch(err => {
      return Promise.reject(err)
    })
}

const getSeriesProducts = (id: string, offset: number, limit: number) => {
  return apolloClient
    .query<SimilarProductResponse>({
      query: similarProductsQuery,
      variables: {
        id: id,
        offset: offset,
        limit: limit,
        type: SIMILAR_PRODUCT_TYPES.SERIES
      }
    }).then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      const data = formatProductData(res.data.similarProductsBy)
      return Promise.resolve(data)
    }).catch(err => {
      return Promise.reject(err)
    })
}

const getProductSkuData = (variables: QueryProductByPrintableSkuArgs) => {
  if (getProductSkuDataSubscription)
    getProductSkuDataSubscription.unsubscribe()
  const query = apolloClient
    .watchQuery<ProductDetailResponse>({
      query: productSkuQuery,
      variables: variables,
      fetchPolicy: 'no-cache'
    })
  return new Promise((resolve, reject) => {
    getProductSkuDataSubscription = query.subscribe(({ data }) => {
      return resolve(data.productByPrintableSku)
    }, err => {
      return reject(err)
    })
  }).finally(() => {
    getProductSkuDataSubscription = undefined
  })
}

const getProductDetailData = (variables: QueryProductByPrintableSkuArgs) => {
  if (getProductSkuDataSubscription)
    getProductSkuDataSubscription.unsubscribe()
  return executeQuery(productDetailQuery, variables, true)
}

const changeEstimateInputs = (variables: QueryProductByPrintableSkuArgs) => {
  if (changeEstimateInputsSubscription)
    changeEstimateInputsSubscription.unsubscribe()
  const query = apolloClient
    .watchQuery<ProductDetailResponse>({
      query: productEstimateQuery,
      variables: variables,
      fetchPolicy: 'no-cache'
    })
  return new Promise((resolve, reject) => {
    changeEstimateInputsSubscription = query.subscribe(({ data }) => {
      return resolve(data.productByPrintableSku)
    }, err => {
      return reject(err)
    })
  }).finally(() => {
    changeEstimateInputsSubscription = undefined
  })
}

const addToEstimate = (attributes: CreateEstimateItemAttributes) => {
  return apolloClient.mutate({
    mutation: addToEstimateMutation,
    variables: {
      input: {
        attributes
      }
    }
  }).then(res => {
    const error = res.errors
    if (error) {
      return Promise.reject(error)
    }
    return Promise.resolve(true)
  }).catch(err => {
    return Promise.reject(err)
  })
}

const getAddonProducts = (productId: number | string) => {
  return apolloClient
    .query<AddonProductResponse>({
      query: addonProductsQuery,
      variables: { productId }
    }).then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      return Promise.resolve(res.data.addonProducts)
    }).catch(err => {
      return Promise.reject(err)
    })
}

const getAdditionalOptions = (productId: number | string) => {
  return apolloClient
    .query<AdditionalOptionsResponse>({
      query: additionalOptionsQuery,
      variables: { productId }
    }).then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      return Promise.resolve(res.data.productOptions)
    }).catch(err => {
      return Promise.reject(err)
    })
}

const getPrefectures = () => {
  return executeQuery(prefecturesQuery)
}


const getDeliveryDates = (variables: QueryProductByPrintableSkuArgs) => {
  return executeQuery(deliveryDatesQuery, variables)
}

const getProductRelatedCategories = (productId: string) => {
  return executeQuery(productRelatedCategoriesQuery, { productId })
}

export const requestRestock = async (input: CreateUserSizeRequestInput) => {
  const data = await executeMutation<CreateUserSizeRequestInput>(requestRestockSizeMutation, input).catch(err => { throw err })
  if (data.createUserSizeRequest?.errors?.length) throw data.createUserSizeRequest?.errors
  return data.createUserSizeRequest
}


export default {
  getSimilarProducts,
  getSeriesProducts,
  getProductSkuData,
  getProductDetailData,
  changeEstimateInputs,
  addToEstimate,
  getAddonProducts,
  getPrefectures,
  getDeliveryDates,
  getAdditionalOptions,
  getProductRelatedCategories
}
